import { Direction } from '@angular/cdk/bidi';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ComponentType } from '@angular/cdk/portal';
import { Component, DoCheck, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarConfig as MatSnackBarConfig,
} from '@angular/material/legacy-snack-bar';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { AutocompleteCategory } from '@grpc-clients/external/mercury/v1/entertainment/entertainment.pb';
import { Store } from '@ngrx/store';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { EntertainmentForm } from '@pages/entertainment/models/entertainment-tickets-config';
import { PreferencesPopupComponent } from '@pages/preferences/components/preferences-popup/preferences-popup.component';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { appendAdobeAnalyticsScriptForProduction } from '@shared/external-scripts/append-adobe-analytics-script';
import { appendOneTrustCookieAdobeScript } from '@shared/external-scripts/append-cookie-adobe-data-layer-script';
import { appendDCRCookieConsentScript } from '@shared/external-scripts/append-dcr-cookie-consent-script';
import {
  appendPricelessSDKScript,
  appendPricelessScript,
} from '@shared/external-scripts/append-mtls-priceless-donate-widget';
import { ClientPath } from '@shared/models/client-path.enum';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { FollowCard } from '@shared/models/preferences';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { ChatData } from '@shared/services/genesys-chat.service';
import { LoaderService } from '@shared/services/loader.service';
import { PreferencesService } from '@shared/services/preferences.service';
import { RoutingStateService } from '@shared/services/route-history.service';
import { ScriptService } from '@shared/services/script.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { SiteTourService } from '@shared/services/site-tour.service';
import { OnboardingDialogClose } from '@shared/shared/enums/onboarding-dialog-close';
import { OnboardingStep } from '@shared/shared/enums/onboarding-step';
import { BaseComponent } from '@shared/shared/helpers/base-component';
import { IMemberProfile } from '@shared/shared/interfaces/member-profile.interfaces';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { IUser } from '@shared/shared/interfaces/user.interfaces';
import { BreadcrumbService } from '@shared/shared/services/breadcrumb-service';
import { LanguageService } from '@shared/shared/services/language-service';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import * as fromGlobalStore from '@shared/store';
import * as CustomAssetStore from '@shared/store/customizable-assets';
import { CustomAssetsState } from '@shared/store/customizable-assets/reducers/custom-assets.reducers';
import { UpdateTicketForm } from '@shared/store/entertainment';
import { ClearGenericApiErrorCode } from '@shared/store/error/api-errors.action';
import { ApiErrorsState, selectGenericApiErrorCode } from '@shared/store/error/api-errors.reducer';
import * as MemberProfileStore from '@shared/store/member-profile';
import { MemberProfileState } from '@shared/store/member-profile/reducers/member-profile.reducers';
import * as routerActions from '@shared/store/router/actions';
import { setHeaderHeight } from '@shared/store/shared';
import * as travelStore from '@shared/store/travel/index';
import * as UserStore from '@shared/store/user';
import { Angulartics2Segment } from 'angulartics2';
import { CompleteMemberProfilePopupComponent } from 'core/components/complete-member-profile-popup/complete-member-profile-popup.component';
import { ContinuePopupComponent } from 'core/components/continue-popup/continue-popup.component';
import { MarketingPopupComponent } from 'core/components/marketing-popup/marketing-popup.component';
import { OnboardingPopupComponent } from 'core/components/onboarding-popup/onboarding-popup.component';
import { WelcomeVideoComponent } from 'core/components/welcome-video/welcome-video.component';
import { inspectFocusedElement } from 'extract-css-variables';
import * as mapboxgl from 'mapbox-gl';
import { CookieService } from 'ngx-cookie-service';
import { TourService } from 'ngx-ui-tour-md-menu';
import { EMPTY, Observable, Subject, Subscription, combineLatest, fromEvent, merge, of } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators';
import {
  ARTIST_POPUP_SHOWN,
  PREFERENCES_POPUP_SHOWN,
  USER_ACCESS_TOKEN_REFRESH_INTERVAL,
  USER_LOCAL_STORAGE_KEY,
  USER_SELECTED_SITE,
  WELCOME_VIDEO_SHOWN,
} from './app.config';

@Component({
  selector: 'ten-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends BaseComponent implements OnInit, OnDestroy, DoCheck {
  public componentData: {
    layout: string;
    userAccessTokenRefreshInterval: number;
    sessionTimeoutInMinutes: number;
    customTenFooter: boolean;
    customMtlsFooter: boolean;
  };
  public cookiesComponentData: {
    default: {
      dcrPolicyCode: string;
    };
  };
  public memberProfileComponentData: {
    default: {
      useBottomSheet?: boolean;
    };
  };
  public isHoldingPage = false;
  public currentRoute;
  public genericApiErrorCode: Observable<number> = this.ngrxStore.select(selectGenericApiErrorCode);
  public customisableVariables$: Observable<CustomisableVariables> = this.customVariablesService.variables;

  public subscription: Subscription;
  public unsubscribe$ = new Subject<void>();
  public destroy$ = new Subject<void>();

  /**
   * A flag to control global app header visibility.
   */
  public isAppHeaderVisible = true;

  // Flag to decide to show legacy bell and popup contact form.
  public conciergeBriefing = false;
  public hasWorkgroup = false;

  public direction = document.querySelector('html')?.dir as Direction;

  public manageCookieBtnExists = true;

  public member_profile$: Observable<MemberProfileState> = this.ngrxStore
    .select(MemberProfileStore.getMemberProfileFeatureState)
    .pipe(
      takeUntil(this.unsubscribe$),
      tap((member) => ((this.ten_opt_in = member.ten_opt_in), (this.viewedTour = member.viewed_tour)))
    );
  public user$: Observable<IUser> = this.ngrxStore.select(UserStore.getUserFeatureState);
  public user_authenticated$: Observable<boolean> = this.ngrxStore
    .select(UserStore.getUserAuthenticated)
    .pipe(delay(0));
  public siteSettings$ = this.siteSettingsService.fetchSiteSettings();
  public genesysChatData$: Observable<ChatData>;
  public isMemberProfilePopupOpen = false;
  private ten_opt_in = false;

  public showTourAgain = false;
  private tourHasBeenRun = false;
  public viewedTour: OnboardingStep;
  public isShowPreferencesPopup: boolean = false;

  public skipLinkPath: string;
  public siteLanguage = 'en-gb';
  public dcrCookieLanguage = 'en-gb';
  public isMobile: boolean = this.breakpointObserver.isMatched([Breakpoints.Handset, Breakpoints.Small]);
  public showFooterSiteMap: boolean;
  public loggedOut = false;
  public showContentOutsideNav = false;

  private intervalHandler = null;
  public isHomePage = false;
  private static ICON_PATH = 'assets/images/esg/esg-icon.svg';
  private static ICON_NAME = 'ten-esg';
  private mapboxRTLTextPlugin: boolean;

  public get onFinalTourStep() {
    return this.tourService.steps.indexOf(this.tourService.currentStep) === this.tourService.steps.length - 1;
  }

  private change$ = merge(
    fromEvent(window, 'scroll'),
    fromEvent(window, 'mousemove'),
    fromEvent(window, 'touchmove')
  ).pipe(delay(0), startWith(''), throttleTime(2000));

  @HostListener('window:keyup', ['$event'])
  public keyEventUp(event: KeyboardEvent) {
    setTimeout(() => {
      if (event.key === 'Control') {
        localStorage.setItem('ctrlKeyPressed', 'false');
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  public keyEventDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      localStorage.setItem('ctrlKeyPressed', 'true');
    }
  }

  private inspectMouseMove$ = merge(fromEvent(window, 'mousemove')).pipe(delay(0), startWith(''), throttleTime(2000));

  constructor(
    private ngrxStore: Store<ApiErrorsState | MemberProfileStore.MemberProfileState | UserStore.UserState>,
    public loader: LoadingBarService,
    public loaderService: LoaderService,
    private snackBar: MatSnackBar,
    private meta: Meta,
    public tourService: TourService,
    public siteTourService: SiteTourService,
    private siteSettingsService: SiteSettingsService,
    private customVariablesService: CustomisableVariablesService,
    private languageService: LanguageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService,
    private bottomSheet: MatBottomSheet,
    private materialDialog: MatDialog,
    private angulartics2Segment: Angulartics2Segment,
    private preferencesService: PreferencesService,
    private breakpointObserver: BreakpointObserver,
    public siteConfService: SiteConfigService,
    private zone: NgZone,
    private cookieService: CookieService,
    private scriptService: ScriptService,
    private iconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private routeHistroy: RoutingStateService,
    private cookiesService: CookieService
  ) {
    super(siteConfService, 'breadcrumbs');
    this.currentRoute = window.location.pathname;
    this.routeHistroy.recordHistory();
    const esgIconUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(AppComponent.ICON_PATH);
    this.iconRegistry.addSvgIcon(AppComponent.ICON_NAME, esgIconUrl);
    this.cookiesComponentData = this.siteConfService.getComponentData('cookies');
    this.memberProfileComponentData = this.siteConfService.getComponentData('CompleteMemberProfile');
    this.isHoldingPage = this.siteConfService.getComponentData('holdingPage')?.default?.showHoldingPage;
    this.ngrxStore.dispatch(new CustomAssetStore.GetCustomAssets());
    if (!this.currentRoute.includes('/auto-login')) {
      //prevent navbar flicker if user is being logged out
      if (this.currentRoute === '/selector') {
        this.loggedOut = true;
      } else {
        this.loggedOut = false;
      }

      this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
        if (val instanceof NavigationEnd && (val.url === '/user/login' || val.url === '/selector' || this.loggedOut)) {
          // Close any open modals on logout.
          this.materialDialog.closeAll();
        }
      });

      this.ngrxStore
        .select(fromGlobalStore.getAllCustomAssets)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((assets: CustomAssetsState) => {
          this.injectRemoteCustomImageAssets(assets);
        });

      // set api url & get user
      this.ngrxStore.dispatch(new UserStore.UserViewLocal());
      this.refreshAccessToken();
      setInterval(() => {
        this.refreshAccessToken();
      }, this.componentData.userAccessTokenRefreshInterval || USER_ACCESS_TOKEN_REFRESH_INTERVAL);

      combineLatest([this.siteSettingsService.fetchSiteSettings(), this.user$])
        .pipe(
          filter(([siteSettings]) => !!siteSettings?.id),
          take(1)
        )
        .subscribe(([siteSettings, user]) => {
          if (user.isAuthenticated) {
            this.zone.runOutsideAngular(() => {
              this.change$
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.startActivityMonitoring(siteSettings.session_timeout_in_minutes));
            });
          } else {
            this.ngrxStore.dispatch(new MemberProfileStore.ClearMemberProfile());
            this.tourHasBeenRun = false;
          }
        });

      this.genericApiErrorCode.subscribe((errorCode) => {
        if (errorCode === 500 || errorCode === 400) {
          this.snackBar.openFromComponent(GenericApiErrorComponent, {
            duration: 15000,
          } as MatSnackBarConfig);
        }
        // if there was any error code, clear the store after
        if (errorCode !== undefined) {
          this.ngrxStore.dispatch(new ClearGenericApiErrorCode());
        }
      });

      this.siteSettingsService
        .fetchSiteSettings()
        .pipe(
          filter((siteSettings: ISiteSettings) => !!siteSettings.id),
          take(1)
        )
        .subscribe((siteSettings: ISiteSettings) => {
          if (this.cookieService.get('selectedSite') === '') {
            localStorage.removeItem(USER_SELECTED_SITE);
          }

          this.siteLanguage = this.languageService.preparingSiteLanguageAttribute(siteSettings);

          this.languageService.initLanguageService(this.siteLanguage);

          this.showContentOutsideNav = siteSettings.switches.pre_login_home_page;

          this.conciergeBriefing = siteSettings.switches.concierge_briefing;
          this.showFooterSiteMap = siteSettings.switches.enable_centurion_benefits;
          this.hasWorkgroup = siteSettings.workgroup !== '';
          this.isShowPreferencesPopup = siteSettings.switches.preferences_follow_events_and_entertainment;

          this.setupSiteConfiguration(siteSettings);

          // set favicon
          this.ngrxStore.select(fromGlobalStore.getAllCustomAssets).subscribe((assets: CustomAssetsState) => {
            const areCustomAssetsSetForSite = this.checkAreCustomAssetsSetForSite(assets);
            const faviconToSet = !areCustomAssetsSetForSite
              ? `assets/assets-branded/${siteSettings.fe_configuration.path}/images/favicon.ico`
              : assets.favicon_icon && assets.favicon_icon.file
                ? assets.favicon_icon.file
                : '';

            document.getElementById('fav-icon').setAttribute('href', faviconToSet);
            const touchIconElm = document.createElement('link');
            touchIconElm.setAttribute('rel', 'apple-touch-icon');
            touchIconElm.setAttribute('id', 'fav-icon-apple');
            touchIconElm.setAttribute('href', faviconToSet);
            document.head.appendChild(touchIconElm);
          });
        });

      this.genesysChatData$ = combineLatest([
        this.siteSettingsService.fetchSiteSettings(),
        this.ngrxStore.select(MemberProfileStore.getMemberProfileFeatureState),
      ])
        .pipe(filter(([siteSettings, memberProfile]) => !!siteSettings.id && !!memberProfile.member_id))
        .pipe(
          map(([siteSettings, memberProfile]) => ({
            deploymentId: siteSettings.genesys_web_messenger_deployment_id,
            userData: {
              siteId: siteSettings.id.toString(),
              memberId: memberProfile.member_id.toString(),
              name: memberProfile.full_name,
              phoneNumber: memberProfile.phone_numbers[0]
                ? `+${memberProfile.phone_numbers[0].country_calling_code_data?.code}${memberProfile.phone_numbers[0].number}`
                : '',
              email: memberProfile.email,
            },
          }))
        );

      this.member_profile$.pipe(takeUntil(this.unsubscribe$)).subscribe((memberProfile) => {
        const marketingPopup = this.materialDialog.getDialogById('top-marketing-popup');
        const welcomeVideoPopup = this.materialDialog.getDialogById('top-video-popup');

        if (memberProfile.viewed_tour === OnboardingStep.COMPLETED) {
          if (marketingPopup) {
            this.isCheckedMarketingPopup(marketingPopup);
          }

          if (
            localStorage.getItem(WELCOME_VIDEO_SHOWN) !== 'true' &&
            !!this.siteSettingsService.settings.welcome_video_url
          ) {
            this.selectWelcomeVideo()
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe(() => this.getInitialPopup());
          }

          if (!marketingPopup && !welcomeVideoPopup) {
            this.getInitialPopup();
            return;
          }
        }

        if (memberProfile.viewed_tour === OnboardingStep.STEP_TWO) {
          if (marketingPopup) {
            this.isCheckedMarketingPopup(marketingPopup);
          }
        }
      });

      /*
      You need the member profile to be updated to show or hide the web chat, also to show the bottom sheet potentially if the user logs in
      You need the router events as you need to check if the current path is one of the accepted routes, we however can't use the router to
      check the path as the last emitted router event can't be used in conjunction with the member profile. for example the member profile
      will have updated to have a member_id but the last router event will be blank therefore the bottom sheet would either not show or not
      show when the user navigates round the site to a blocked path
      */
      combineLatest([this.member_profile$, this.router.events, this.user_authenticated$])
        .pipe(
          takeUntil(this.unsubscribe$),
          filter(
            ([memberProfile, , userAuth]) =>
              memberProfile.id !== 0 && userAuth && !memberProfile['loading'] && !this.tourHasBeenRun
          )
        )
        .subscribe((response) => {
          this.tourHasBeenRun = true;
          const memberProfile = { ...response[0] };
          this.completeMemberProfile(memberProfile);
        });
    }

    this.user_authenticated$.pipe(takeUntil(this.unsubscribe$)).subscribe((isAuthenticated) => {
      this.loggedOut = !isAuthenticated;
    });

    // send css var names to parent container (design admin)
    if (window.self !== window.top) {
      this.zone.runOutsideAngular(() => {
        this.inspectMouseMove$.pipe(takeUntil(this.destroy$)).subscribe((e: MouseEvent) => {
          if (e.ctrlKey) {
            inspectFocusedElement(e);
          }
        });
      });
    }
  }

  private checkAreCustomAssetsSetForSite(customAssets: CustomAssetsState): boolean {
    if (!customAssets) return false;
    return !!Object.keys(customAssets).find(
      (key) => key !== 'loading' && key !== 'loaded' && !!customAssets[key] && customAssets[key].length
    );
  }

  public ngOnInit() {
    combineLatest([this.ngrxStore.select(MemberProfileStore.getMemberProfileFeatureState), this.siteSettings$])
      .pipe(
        filter(
          ([memberProfile, siteSettings]) =>
            !!memberProfile &&
            !!memberProfile.email &&
            memberProfile.email.trim() !== '' &&
            !!siteSettings &&
            siteSettings.switches?.enable_wootric_online_survey
        ),
        take(1)
      )
      .subscribe(([memberProfile]) => {
        this.scriptService.appendWootricScript(true, 'NPS-225030ca', memberProfile?.email || '');
      });

    if (!this.currentRoute.includes('/auto-login')) {
      // Used   to set the title and the breadcrumbs
      this.router.events.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
        if (event instanceof NavigationEnd) {
          const result = this.breadcrumbService.getBreadcrumbs(this.activatedRoute.root);
          this.ngrxStore.dispatch(new routerActions.RouterUpdateBreadcrumbs(result));

          this.skipLinkPath = `${location.href.replace('#main-content', '')}#main-content`;
        } else if (event instanceof RoutesRecognized) {
          this.setAppHeaderVisibility(event.url);
          if (!event.url.includes('ds=true')) {
            window.scrollTo(0, 0);
          }
        }
      });

      this.setAppHeaderVisibility(this.router.url);

      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((navEnd: NavigationEnd) => {
        this.currentRoute = navEnd.urlAfterRedirects;
        this.angulartics2Segment.pageTrack(this.currentRoute);

        const regexSelector = /selector/;
        this.loggedOut = regexSelector.test(this.currentRoute);

        if (!navEnd.url.includes('entertainment')) {
          this.cleanupEntertainmentCookie();
        }
      });

      this.ngrxStore.dispatch(new travelStore.ClearFlightClasses());
    }
  }

  private cleanupEntertainmentCookie() {
    const searchForm = this.cookiesService.get('entertainmentTicketForm');
    if (searchForm) {
      this.cookieService.delete('entertainmentTicketForm');
      const form = JSON.parse(searchForm);
      const newSearchForm = {
        // keep location only
        start_location: form.start_location,
        selected_start_location_name: form.selected_start_location_name,
        geolocation: form.geolocation,
        event_type: AutocompleteCategory.EVENT_LOCATION,
      } as any as EntertainmentForm;
      this.cookieService.set('entertainmentTicketForm', JSON.stringify(newSearchForm), 365, '/');
      this.ngrxStore.dispatch(new UpdateTicketForm({ data: newSearchForm, valid: true }));
    }
  }

  public ngDoCheck(): void {
    this.isHomePage = this.currentRoute.includes('/homepage') ? true : false;
  }
  public endSiteTour(skipped: boolean) {
    if (skipped) {
      const currentStep = this.tourService.steps.indexOf(this.tourService.currentStep) + 1;
      const action = `OnboardingSkipSpotlight${currentStep}`;

      this.angulartics2Segment.eventTrack(action, {
        locale: this.languageService.currentLanguage,
        timestamp: Date.now(),
      });
    }
    this.showMarketingPopup();
    this.siteTourService.tourSkipped = this.showTourAgain;
    this.tourService.end();
  }

  public showPreferencesPopup() {
    this.member_profile$
      .pipe(
        take(1),
        switchMap((memberProfile: IMemberProfile) => {
          const preferencesPopupHasBeenShown = localStorage.getItem(PREFERENCES_POPUP_SHOWN) === 'true';
          const showPreferencesToMember = memberProfile.show_prefs_overlay;
          const switches = this.siteSettingsService.settings.switches;
          const preferencesExist =
            (switches.preferences_artist || switches.preferences_interests) &&
            switches.preferences_follow_events_and_entertainment;

          const showPreferencesPopup = showPreferencesToMember && !preferencesPopupHasBeenShown && preferencesExist;

          return showPreferencesPopup ? this.preferencesService.getRandomPreferences() : EMPTY;
        })
      )
      .subscribe((topPreferences: FollowCard[]) => {
        this.displayInitialPopUp(
          topPreferences,
          true,
          this.siteSettingsService.settings.switches.preferences_interests
        );
      });

    if (!this.mapboxRTLTextPlugin) {
      mapboxgl.setRTLTextPlugin(
        'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js',
        null,
        true // Lazy load the plugin
      );
      this.mapboxRTLTextPlugin = true;
    }
  }

  public storeHeaderHeight(rect: DOMRect) {
    this.ngrxStore.dispatch(setHeaderHeight({ height: rect.height }));
  }

  public displayInitialPopUp(data: FollowCard[], isPreferencesPopup: boolean, preferencesInterests: boolean) {
    if (this.materialDialog.openDialogs.length === 0 && data.length) {
      const dialogRef = this.materialDialog.open(PreferencesPopupComponent, {
        ariaLabel: 'tell us what you are interested in dialog',
        maxHeight: '90vh',
        width: '732px',
        panelClass: isPreferencesPopup ? 'top-preferences-popup' : 'top-artist-popup',
        id: isPreferencesPopup ? 'top-preferences-popup' : 'top-artist-popup',
        data,
        direction: document.querySelector('html')?.dir as Direction,
        position: this.isMobile ? { bottom: '0' } : undefined,
      });
      dialogRef.componentInstance.isPreferencesPopup = isPreferencesPopup;
      dialogRef.componentInstance.isInterestsPreferences = preferencesInterests;
      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() =>
          localStorage.setItem(isPreferencesPopup ? PREFERENCES_POPUP_SHOWN : ARTIST_POPUP_SHOWN, 'true')
        );
    }
  }

  public showArtistPopup() {
    this.member_profile$
      .pipe(
        take(1),
        switchMap((memberProfile: IMemberProfile) => {
          const artistsPopupHasBeenShown = localStorage.getItem(ARTIST_POPUP_SHOWN) === 'true';
          const showPreferencesToMember = memberProfile.show_prefs_overlay;
          const switches = this.siteSettingsService.settings.switches;
          const preferencesExist = switches.preferences_artist;

          const showArtistPopup = showPreferencesToMember && !artistsPopupHasBeenShown && preferencesExist;

          return showArtistPopup ? this.preferencesService.getRandomArtists(9, 1) : EMPTY;
        })
      )
      .subscribe((topArtists: FollowCard[]) => {
        if (topArtists.length >= 3) {
          this.displayInitialPopUp(topArtists, false, false);
        }
      });
  }

  /**
   * Set app header visibility based on the current URL passed in.
   *
   * When any of the url segments are matched,
   * the header visibility flag is set to false.
   */
  public setAppHeaderVisibility(url: string) {
    const regexExisting = /existing/;
    const regexBriefing = /briefing/;

    this.isAppHeaderVisible = !(
      regexBriefing.test(url) ||
      // regexHotTablesPage.test(url) ||
      regexExisting.test(url)
    );
  }

  public setupSiteConfiguration(siteSettings: ISiteSettings) {
    let content: string;

    // Will update meta description and keywords per client and ensure only single spaces exist
    content = siteSettings.fe_configuration.meta_description.replace(/\s\s+/g, ' ');
    this.meta.updateTag({ name: 'description', content });

    content = siteSettings.fe_configuration.meta_keywords.replace(/\s\s+/g, ' ');
    this.meta.updateTag({ name: 'keywords', content });

    this.setupSiteCookieScripts(siteSettings);
  }

  public setupSiteCookieScripts(siteSettings: ISiteSettings) {
    const isUserLoggedIn = !!localStorage.getItem(USER_LOCAL_STORAGE_KEY);

    this.handleOneTrustScripts(siteSettings, isUserLoggedIn);
    this.handleGhostryScripts(siteSettings, isUserLoggedIn);
    this.handleVisaScripts(siteSettings, isUserLoggedIn);
    this.handleMasterCardGroupScripts(siteSettings);
  }

  private handleOneTrustScripts(siteSettings: ISiteSettings, isUserLoggedIn: boolean): void {
    const path = siteSettings.fe_configuration.path;
    const languageFromHTML = this.isMasterCardGroup(path);
    const hasCookieSettingId = !!siteSettings.cookie_setting_id;

    if (path === ClientPath.COUTTS) {
      this.scriptService.appendOneTrustCookieConsentScript(
        siteSettings.fe_configuration.segment_script_id,
        path,
        hasCookieSettingId,
        languageFromHTML
      );
      this.setupCookieButton(isUserLoggedIn);
    } else {
      if (!hasCookieSettingId) {
        this.scriptService.appendYoutubeScript(hasCookieSettingId);
        this.scriptService.appendSegmentScript(hasCookieSettingId, siteSettings.fe_configuration.segment_script_id);
      } else {
        this.scriptService.appendTenOneTrustCookieConsentScript(
          siteSettings.fe_configuration.segment_script_id,
          siteSettings.cookie_setting_id,
          languageFromHTML
        );
        this.setupCookieButton(isUserLoggedIn);
      }
    }
  }

  private setupCookieButton(isUserLoggedIn: boolean) {
    this.manageCookieBtnExists = !!document.getElementById('ot-sdk-btn');
    if (!this.manageCookieBtnExists && location.host !== 'travel-test.mastercard.com') {
      const element = document.createElement('button');
      element.id = 'ot-sdk-btn';
      element.className = 'ot-sdk-show-settings';
      element.innerHTML = 'Manage Cookies';
      document.body.append(element);
    }

    if (isUserLoggedIn && document.getElementById('ot-sdk-btn')) {
      document.getElementById('ot-sdk-btn').style.display = 'none';
    }
  }

  private handleGhostryScripts(siteSettings: ISiteSettings, isUserLoggedIn: boolean): void {
    if (siteSettings.fe_configuration.ghostry_cookie_consent_script_active) {
      if (!isUserLoggedIn) {
        appendOneTrustCookieAdobeScript();

        const interval = setInterval(() => {
          const element = document.getElementById('_evh-ric');
          if (element) {
            const parent = document.createElement('div');
            parent.classList.add('overlay-parent');

            const child = document.createElement('div');
            child.classList.add('overlay-child');

            parent.append(child);
            element.insertBefore(parent, element.firstChild);

            clearInterval(interval);
          }
        }, 200);
      }
    }
  }

  private handleVisaScripts(siteSettings: ISiteSettings, isUserLoggedIn: boolean): void {
    const path = siteSettings.fe_configuration.path;
    if (
      path === ClientPath.VISA_CEMEA ||
      (isUserLoggedIn && path === ClientPath.VISA_LAC) ||
      (isUserLoggedIn && path === ClientPath.BANCO_PICHINCHA)
    ) {
      this.dcrCookieLanguage = this.siteLanguage.includes('pt')
        ? 'pt-br'
        : this.siteLanguage.includes('es')
          ? 'es'
          : this.dcrCookieLanguage;
      appendDCRCookieConsentScript(this.dcrCookieLanguage, this.cookiesComponentData?.default?.dcrPolicyCode);
    }
  }

  private handleMasterCardGroupScripts(siteSettings: ISiteSettings): void {
    const path = siteSettings.fe_configuration.path;

    if (path === ClientPath.MASTERCARD_RAK || path === ClientPath.MASTERCARD_CITI) {
      appendAdobeAnalyticsScriptForProduction();
    }

    if (this.isMasterCardGroupWithPriceless(path) && siteSettings.switches.enable_mtls_priceless_planet_widget) {
      if (siteSettings.mtls_priceless_planet_script) {
        appendPricelessSDKScript();
        appendPricelessScript(siteSettings.mtls_priceless_planet_script);
      } else {
        console.warn('[MTLS Priceless Widget] Switch enabled but script is empty.');
      }
    }
  }

  private isMasterCardGroup(path: string): boolean {
    return (
      path === ClientPath.ITAU ||
      path === ClientPath.SNBAR ||
      path === ClientPath.SNBEN ||
      path === ClientPath.MASTERCARD ||
      path === ClientPath.MASTERCARD_CITI ||
      path === ClientPath.MASTERCARD_HNW ||
      path === ClientPath.MASTERCARD_RAK ||
      path === ClientPath.MASTERCARD_RAK_BANK ||
      path === ClientPath.MASTERCARD_NEO
    );
  }

  private isMasterCardGroupWithPriceless(path: string): boolean {
    return path === ClientPath.MASTERCARD || path === ClientPath.MASTERCARD_RAK || path === ClientPath.MASTERCARD_CITI;
  }

  /**
   * Shows the bottom sheet for the member to either fill in their title or to accept the terms
   * and conditions
   * @param memberProfile
   */
  public completeMemberProfile(memberProfile: IMemberProfile) {
    const acceptedRoutes = [
      '/privacy',
      '/terms',
      '/product/privacy',
      '/product/terms',
      '/product/accessibility',
      '/product/faq',
      '/product/contact',
      'error/login-failure',
      'logout',
      'error/rate-limitation',
    ];
    let popupRef: MatBottomSheetRef<any> | MatDialogRef<any>;
    if (
      !acceptedRoutes.includes(window.location.pathname) &&
      (memberProfile.terms_and_conditions === false ||
        memberProfile.title === '' ||
        memberProfile.email === '' ||
        memberProfile.email === null) &&
      !this.isMemberProfilePopupOpen
    ) {
      const memberProfileData = {
        firstName: memberProfile.first_name,
        lastName: memberProfile.last_name,
        termsAndConditions: memberProfile.terms_and_conditions,
        tenOptIn: memberProfile.ten_opt_in,
        title: memberProfile.title,
        email: memberProfile.email,
      };
      if (this.memberProfileComponentData?.default?.useBottomSheet) {
        popupRef = this.bottomSheet.open(CompleteMemberProfilePopupComponent, {
          data: memberProfileData,
          panelClass: 'complete-memberprofile-popup-wrapper',
          disableClose: true,
          direction: document.querySelector('html')?.dir as Direction,
        });

        popupRef.afterDismissed().subscribe(() => this.handlePopupClose(memberProfile));
      } else {
        popupRef = this.materialDialog.open(CompleteMemberProfilePopupComponent, {
          data: memberProfileData,
          panelClass: 'complete-memberprofile-popup-wrapper',
          disableClose: true,
          direction: document.querySelector('html')?.dir as Direction,
        });
        popupRef.afterClosed().subscribe(() => this.handlePopupClose(memberProfile));
      }
    } else if (
      memberProfile.terms_and_conditions &&
      memberProfile.title !== '' &&
      memberProfile.viewed_tour !== OnboardingStep.COMPLETED
    ) {
      this.selectWelcomeVideo().subscribe(() => {
        this.selectTourStart(memberProfile);
      });
    }
  }

  public handlePopupClose(memberProfile: IMemberProfile) {
    this.isMemberProfilePopupOpen = false;
    if (memberProfile.viewed_tour !== OnboardingStep.COMPLETED) {
      this.selectWelcomeVideo().subscribe(() => {
        this.selectTourStart(memberProfile);
      });
    }
  }
  public selectTourStart(memberProfile: IMemberProfile) {
    if (this.siteSettingsService.settings.switches.show_tour) {
      if (memberProfile.viewed_tour === OnboardingStep.STEP_ONE) {
        this.showOnboardingSlides();
      } else if (memberProfile.viewed_tour === OnboardingStep.STEP_TWO) {
        this.siteTourService.start();
      }
    }
  }

  /**
   * Shows the onboarding popup
   */
  public showOnboardingSlides() {
    const onboardingDialogRef = this.openModal(OnboardingPopupComponent, {
      ariaLabel: 'onboarding tour',
    });
    onboardingDialogRef.afterClosed().subscribe((res) => {
      this.user_authenticated$.pipe(take(1)).subscribe((authenticated) => {
        this.ngrxStore.dispatch(
          new MemberProfileStore.UpdateMemberProfile({
            viewed_tour: OnboardingStep.STEP_TWO,
          })
        );

        if (res === OnboardingDialogClose.SKIP) {
          const marketingPopup = this.showMarketingPopup();
          if (marketingPopup) {
            marketingPopup.afterClosed().subscribe((type: string) => {
              if (type === OnboardingDialogClose.DISAGREED || type === OnboardingDialogClose.CONTINUE) {
                this.getInitialPopup();
                return;
              }
            });
          } else {
            this.getInitialPopup();
          }
        }

        if (res === OnboardingDialogClose.END && authenticated) {
          const continuePopup = this.openModal(ContinuePopupComponent);
          continuePopup.afterClosed().subscribe((response: string) => {
            if (response === OnboardingDialogClose.DISCOVER) {
              this.siteTourService.start();
            }

            if (response === OnboardingDialogClose.NEXT) {
              this.showMarketingPopup();
            }
          });
        }
      });
    });
  }

  public showMarketingPopup(): MatDialogRef<MarketingPopupComponent> {
    if (this.ten_opt_in) return;
    return this.openModal(MarketingPopupComponent);
  }

  /**
   * Opens a material modal and returns the ref
   * @param component
   */
  public openModal<T>(component: ComponentType<T>, config: MatDialogConfig = {}): MatDialogRef<T> {
    return this.materialDialog.open<T>(component, {
      direction: document.querySelector('html')?.dir as Direction,
      disableClose: true,
      maxWidth: '90vw',
      id: 'top-marketing-popup',
      ...config,
    });
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshAccessToken() {
    this.user$.pipe(take(1)).subscribe((data) => {
      if (data.isAuthenticated) {
        this.ngrxStore.dispatch(new UserStore.UserRefreshAccessToken());
      }
    });
  }

  private startActivityMonitoring(sessionTimeoutInMinutes: number) {
    if (!sessionTimeoutInMinutes) {
      sessionTimeoutInMinutes = this.componentData.sessionTimeoutInMinutes;
    }

    localStorage.setItem('userActivityTimestamp', Math.floor(Date.now() / 1000).toString());

    clearInterval(this.intervalHandler);
    this.checkSessionTimeout(sessionTimeoutInMinutes);
    this.intervalHandler = setInterval(() => {
      this.checkSessionTimeout(sessionTimeoutInMinutes);
    }, 1000);
  }

  private checkSessionTimeout(sessionTimeoutInMinutes: number) {
    const userActivityTimestamp = +localStorage.getItem('userActivityTimestamp');
    const diffInSeconds = Math.floor(Date.now() / 1000) - userActivityTimestamp;

    if (diffInSeconds >= sessionTimeoutInMinutes * 60 - 20) {
      //leave enough time to wrap up session on FE before it expires
      this.zone.run(() => {
        this.destroy$.next();
        this.destroy$.complete();
        clearInterval(this.intervalHandler);
        this.ngrxStore.dispatch(new UserStore.UserLogout());
        this.router.navigateByUrl('/user/login');
      });
    }
  }

  private injectRemoteCustomImageAssets(assets: CustomAssetsState) {
    if (assets?.logged_out_landing_usp_image_for_main) {
      document.documentElement.style.setProperty(
        '--ten-homepage-hero-image',
        `url(${assets.logged_out_landing_usp_image_for_main.file})`
      );
    }
    if (assets.logged_out_landing_usp_image_for_hotels) {
      document.documentElement.style.setProperty(
        '--ten-homepage-travel-image',
        `url(${assets.logged_out_landing_usp_image_for_hotels.file})`
      );
      document.documentElement.style.setProperty(
        '--ten-homepage-hotel-image',
        `url(${assets.logged_out_landing_usp_image_for_hotels.file})`
      );
    }
    if (assets?.logged_out_landing_usp_image_for_dining) {
      document.documentElement.style.setProperty(
        '--ten-homepage-dining-image',
        `url(${assets.logged_out_landing_usp_image_for_dining.file})`
      );
    }
    if (assets.logged_out_landing_usp_image_for_entertainment) {
      document.documentElement.style.setProperty(
        '--ten-homepage-hot-tickets-image',
        `url(${assets.logged_out_landing_usp_image_for_entertainment.file})`
      );
    }
    if (assets.logged_out_landing_usp_image_for_offers) {
      document.documentElement.style.setProperty(
        '--ten-homepage-member-benefits-image',
        `url(${assets.logged_out_landing_usp_image_for_offers.file})`
      );
    }
    if (assets.logged_out_landing_usp_image_for_events) {
      document.documentElement.style.setProperty(
        '--ten-homepage-member-events-image',
        `url(${assets.logged_out_landing_usp_image_for_events.file})`
      );
    }
    if (assets.logged_out_landing_usp_image_for_inspiration) {
      document.documentElement.style.setProperty(
        '--ten-homepage-inspiration-image',
        `url(${assets.logged_out_landing_usp_image_for_inspiration.file})`
      );
    }
  }

  private selectWelcomeVideo(): Observable<any> {
    if (!!this.siteSettingsService.settings.welcome_video_url && localStorage.getItem(WELCOME_VIDEO_SHOWN) !== 'true') {
      return this.user_authenticated$.pipe(
        take(1),
        distinctUntilChanged(),
        switchMap((res) => {
          if (res && typeof YT !== 'undefined') {
            return this.materialDialog
              .open(WelcomeVideoComponent, {
                maxWidth: 'none',
                panelClass: 'welcome-video-popup',
                autoFocus: false,
                data: this.siteSettingsService.settings.welcome_video_url,
                direction: document.querySelector('html')?.dir as Direction,
                disableClose: true,
                id: 'top-video-popup',
              })
              .afterClosed()
              .pipe(
                tap(() => {
                  localStorage.setItem(WELCOME_VIDEO_SHOWN, 'true');
                })
              );
          } else {
            return of(EMPTY);
          }
        })
      );
    } else {
      return of(EMPTY);
    }
  }

  public getInitialPopup(): void {
    this.isShowPreferencesPopup ? this.showPreferencesPopup() : this.showArtistPopup();
  }

  public isCheckedMarketingPopup(marketingPopup: MatDialogRef<MarketingPopupComponent>): void {
    marketingPopup
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((type: string) => {
        if (type === OnboardingDialogClose.DISAGREED || type === OnboardingDialogClose.CONTINUE) {
          this.getInitialPopup();
          return;
        }
      });
  }
}
